import { debounce } from "lodash";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import RateAgreementService from "../services/RateAgreementService";
import UtilService from "../services/UtilService";
import { RateData } from "../types/RateData";
import { useRateAgreementTenantConfiguration } from "./TenantContext";

type SettlementFeeContextType = {
    value?: number;
    isInitialized: boolean;
    requestIsPending: boolean;
    requestHasFailed: boolean;
    update: (rateData: RateData) => Promise<void>;
};

const SETTLEMENT_FEE_DEBOUNCE_TIMEOUT_MS = 500;

export const SettlementFeeContext = createContext<SettlementFeeContextType | undefined>(undefined);

export const SettlementFeeProvider: React.FC = ({ children }) => {
    const configuration = useRateAgreementTenantConfiguration();

    const [settlementFee, setSettlementFee] = useState<number>();
    const [requestHasFailed, setRequestHasFailed] = useState<boolean>(false);
    const [requestIsPending, setRequestIsPending] = useState<boolean>(false);

    useEffect(() => {
        fetchInitialSettlementFee();
    }, []);

    const fetchInitialSettlementFee = async () => {
        try {
            setRequestIsPending(true);
            setRequestHasFailed(false);
            const debtNumber = await UtilService.getDebtNo();
            const rateAgreementOptions = await RateAgreementService.getRateAgreementOptions(debtNumber);
            setSettlementFee(rateAgreementOptions.settlementFee);
            setRequestIsPending(false);
        } catch (error) {
            setRequestHasFailed(true);
            console.error("Error fetching initial settlement fee:", error);
        } finally {
            setRequestIsPending(false);
        }
    };

    const updateSettlementFee = async (rateData: RateData) => {
        if (!configuration?.enableDynamicSettlementFee) {
            console.log("Feature toggle for dynamic settlement fee disabled. Skipping update.");
            return;
        }

        setRequestIsPending(true);
        setRequestHasFailed(false);

        await fetchSettlementFeeDebounced(rateData);
    };

    const fetchSettlementFeeDebounced = useCallback(
        debounce(
            async (rateData: RateData) => {
                try {
                    const response = await RateAgreementService.getSettlementFee(rateData);
                    setRequestIsPending(false);
                    setSettlementFee(response.fee);
                } catch (error) {
                    setRequestHasFailed(true);
                    console.error("Error updating settlement fee:", error);
                }
            },
            SETTLEMENT_FEE_DEBOUNCE_TIMEOUT_MS,
            {}
        ),
        []
    );

    return (
        <SettlementFeeContext.Provider
            value={{
                value: settlementFee,
                isInitialized: configuration?.enableDynamicSettlementFee !== undefined,
                requestHasFailed,
                requestIsPending,
                update: updateSettlementFee,
            }}>
            {children}
        </SettlementFeeContext.Provider>
    );
};

export const useSettlementFee = (): SettlementFeeContextType => {
    const context = useContext(SettlementFeeContext);
    if (context === undefined) {
        throw new Error("useSettlementFee must be used within a SettlementFeeProvider");
    }
    return context;
};
